import { utcToZonedTime, format } from "date-fns-tz";
import { formatCurrency } from "../../lib/currency/currency";
import { FBT } from "../../lib/i18n/i18n_provider";
import { getSystemLocale } from "../../lib/locale/locale";
import { getFirstDayOfNextMonthAfterNMonths } from "../../offering/date_utils";
import { monthDifference, parseDay } from "../../offering/day_utils";
import { Step } from "../components/steps_membership";
import { getMembershipCheckoutTimelineOnTodayStepText } from "./get_membership_steps_today_helper_text";

interface GetHasManyZeroPriceMonthsFromTodayConfig {
  todayPrice: number;
  recurringPriceWithPromoIteration: number;
}

function getHasManyZeroPriceMonthsFromToday(
  config: GetHasManyZeroPriceMonthsFromTodayConfig
) {
  const { todayPrice, recurringPriceWithPromoIteration } = config;

  return todayPrice === recurringPriceWithPromoIteration && todayPrice === 0;
}

interface GetMembershipCheckoutBoxTimelineStepsConfig {
  subscriptionIterations?: number;
  firstPaymentCycles?: number;
  membershipStartDateString?: string;
  currency: string | null;
  subscriptionPromotionEndsAt?: number;
  recurringPrice: number;
  fbt: FBT;
  todayPrice: number;
  recurringPriceWithPromoIteration: number;
  promoMonthsLeftAfterFirstPayment: number;
  timeZone: string;
}

export function getMembershipCheckoutBoxTimelineSteps(
  config: GetMembershipCheckoutBoxTimelineStepsConfig
) {
  const {
    subscriptionIterations = 0,
    firstPaymentCycles = 0,
    membershipStartDateString,
    currency,
    subscriptionPromotionEndsAt: promotionEnd,
    recurringPrice,
    todayPrice,
    recurringPriceWithPromoIteration,
    promoMonthsLeftAfterFirstPayment,
    fbt,
    timeZone,
  } = config;

  if (firstPaymentCycles === 0 && promoMonthsLeftAfterFirstPayment === 0) {
    return [];
  }

  const subscriptionPromotionEndsAtDateTime = utcToZonedTime(
    promotionEnd ? new Date(promotionEnd) : new Date(),
    timeZone
  );

  const subscriptionPromotionEndsAt = format(
    subscriptionPromotionEndsAtDateTime,
    "MMMM d, yyyy",
    { timeZone }
  );

  const hasManyZeroPriceMonthsFromToday = getHasManyZeroPriceMonthsFromToday({
    todayPrice,
    recurringPriceWithPromoIteration,
  });

  const shouldReturnZeroPriceCollapsedTimeline =
    hasManyZeroPriceMonthsFromToday && promoMonthsLeftAfterFirstPayment > 0;

  if (shouldReturnZeroPriceCollapsedTimeline) {
    const nextRecurringPriceDateAfterPromoMonths =
      getFirstDayOfNextMonthAfterNMonths({
        addedMonths:
          firstPaymentCycles +
          monthDifference(
            new Date(),
            membershipStartDateString
              ? parseDay(membershipStartDateString)
              : new Date()
          ) +
          promoMonthsLeftAfterFirstPayment,

        formatString: "MMMM d, yyyy",
      });
    return getZeroPriceMonthsCollapsedSteps({
      fbt,
      todayPrice,
      currency,
      firstPaymentCycles,
      promoMonthsLeftAfterFirstPayment,
      nextRecurringPriceDate: nextRecurringPriceDateAfterPromoMonths,
      recurringPrice,
    });
  }

  const nextRecurringPriceDateAfterFirstPaymentCycles =
    getFirstDayOfNextMonthAfterNMonths({
      addedMonths:
        firstPaymentCycles +
        monthDifference(
          new Date(),
          membershipStartDateString
            ? parseDay(membershipStartDateString)
            : new Date()
        ),
      formatString: "MMMM d, yyyy",
    });

  if (!subscriptionIterations) {
    return getNonPromoSteps({
      fbt,
      todayPrice,
      currency,
      firstPaymentCycles,
      promoMonthsLeftAfterFirstPayment,
      nextRecurringPriceDate: nextRecurringPriceDateAfterFirstPaymentCycles,
      recurringPrice,
    });
  }

  return getNonCollapsiblePromotionSteps({
    fbt,
    todayPrice,
    currency,
    firstPaymentCycles,
    promoMonthsLeftAfterFirstPayment,
    nextRecurringPriceDate: nextRecurringPriceDateAfterFirstPaymentCycles,
    recurringPrice,
    subscriptionIterations,
    membershipStartDateString,
    recurringPriceWithPromoIteration,
    subscriptionPromotionEndsAt,
  });
}

interface GetZeroPriceMonthsCollapsedStepsConfig {
  fbt: FBT;
  todayPrice: number;
  currency: string | null;
  firstPaymentCycles?: number;
  promoMonthsLeftAfterFirstPayment: number;
  nextRecurringPriceDate: string;
  recurringPrice: number;
}

function getZeroPriceMonthsCollapsedSteps({
  fbt,
  todayPrice,
  currency,
  firstPaymentCycles,
  promoMonthsLeftAfterFirstPayment,
  nextRecurringPriceDate,
  recurringPrice,
}: GetZeroPriceMonthsCollapsedStepsConfig): Step[] {
  return [
    {
      leftText: fbt("Today"),
      rightText: formatCurrency(todayPrice, currency, getSystemLocale()),
      isCompleted: true,
      helperText: fbt(
        getMembershipCheckoutTimelineOnTodayStepText({
          firstPaymentCycle: firstPaymentCycles,
          appendTheNextPromoMonthsText: true,
          promoMonths: promoMonthsLeftAfterFirstPayment,
        })
      ),
    },
    {
      leftText: nextRecurringPriceDate,
      rightText: formatCurrency(recurringPrice, currency, getSystemLocale()),
      isCompleted: false,
      helperText: fbt("Monthly payment"),
    },
  ];
}

interface GetNonCollapsiblePromotionStepsConfig
  extends GetZeroPriceMonthsCollapsedStepsConfig {
  subscriptionIterations: number;
  membershipStartDateString?: string;
  recurringPriceWithPromoIteration: number;
  subscriptionPromotionEndsAt?: string;
}

function getNonCollapsiblePromotionSteps({
  fbt,
  todayPrice,
  currency,
  firstPaymentCycles = 0,
  promoMonthsLeftAfterFirstPayment,
  recurringPrice,
  subscriptionIterations,
  membershipStartDateString,
  recurringPriceWithPromoIteration,
  subscriptionPromotionEndsAt,
}: GetNonCollapsiblePromotionStepsConfig) {
  const promoSteps = getPromotionSteps({
    fbt,
    currency,
    firstPaymentCycles,
    recurringPrice,
    subscriptionIterations,
    membershipStartDateString,
    recurringPriceWithPromoIteration,
    subscriptionPromotionEndsAt,
    promoMonthsLeftAfterFirstPayment,
  });

  return [
    {
      leftText: fbt("Today"),
      rightText: formatCurrency(todayPrice, currency, getSystemLocale()),
      isCompleted: true,
      helperText: fbt(
        getMembershipCheckoutTimelineOnTodayStepText({
          firstPaymentCycle: firstPaymentCycles,
          appendTheNextPromoMonthsText: false,
          promoMonths: promoMonthsLeftAfterFirstPayment,
        })
      ),
    },
    ...promoSteps,
  ];
}

interface GetNonPromoStepsConfig {
  fbt: FBT;
  todayPrice: number;
  currency: string | null;
  firstPaymentCycles?: number;
  promoMonthsLeftAfterFirstPayment: number;
  nextRecurringPriceDate: string;
  recurringPrice: number;
}

function getNonPromoSteps({
  fbt,
  todayPrice,
  currency,
  firstPaymentCycles = 0,
  promoMonthsLeftAfterFirstPayment,
  nextRecurringPriceDate,
  recurringPrice,
}: GetNonPromoStepsConfig) {
  return [
    {
      leftText: fbt("Today"),
      rightText: formatCurrency(todayPrice, currency, getSystemLocale()),
      isCompleted: true,
      helperText: fbt(
        getMembershipCheckoutTimelineOnTodayStepText({
          firstPaymentCycle: firstPaymentCycles,
          appendTheNextPromoMonthsText: false,
          promoMonths: promoMonthsLeftAfterFirstPayment,
        })
      ),
    },
    {
      leftText: nextRecurringPriceDate,
      rightText: formatCurrency(recurringPrice, currency, getSystemLocale()),
      isCompleted: false,
      helperText: fbt("Monthly payment"),
    },
  ];
}

interface GetPromotionStepsConfig {
  fbt: FBT;
  currency: string | null;
  firstPaymentCycles?: number;
  recurringPrice: number;
  subscriptionIterations: number;
  membershipStartDateString?: string;
  recurringPriceWithPromoIteration: number;
  subscriptionPromotionEndsAt?: string;
  promoMonthsLeftAfterFirstPayment: number;
}

function getPromotionSteps({
  fbt,
  currency,
  firstPaymentCycles,
  recurringPrice,
  subscriptionIterations,
  membershipStartDateString,
  recurringPriceWithPromoIteration,
  subscriptionPromotionEndsAt,
  promoMonthsLeftAfterFirstPayment,
}: GetPromotionStepsConfig) {
  const promoEndStep = getPromoEndStep({
    fbt,
    currency,
    firstPaymentCycles,
    membershipStartDateString,
    subscriptionIterations,
    recurringPrice,
    subscriptionPromotionEndsAt,
  });

  if (promoMonthsLeftAfterFirstPayment === 0) {
    return [promoEndStep];
  }

  if (promoMonthsLeftAfterFirstPayment === 1) {
    return [
      getSingleMonthPromoAfterTodayStep({
        fbt,
        currency,
        firstPaymentCycles,
        membershipStartDateString,
        recurringPriceWithPromoIteration,
      }),
      promoEndStep,
    ];
  }

  return [
    getMultipleMonthsPromoAfterTodayStep({
      fbt,
      currency,
      promoMonthsLeftAfterFirstPayment,
      recurringPriceWithPromoIteration,
    }),
    promoEndStep,
  ];
}

interface GetMultipleMonthsPromoAfterTodayStepConfig {
  fbt: FBT;
  currency: string | null;
  promoMonthsLeftAfterFirstPayment: number;
  recurringPriceWithPromoIteration: number;
}

function getMultipleMonthsPromoAfterTodayStep({
  fbt,
  currency,
  promoMonthsLeftAfterFirstPayment,
  recurringPriceWithPromoIteration,
}: GetMultipleMonthsPromoAfterTodayStepConfig): Step {
  return {
    leftText: `Next ${promoMonthsLeftAfterFirstPayment} months`,
    rightText: formatCurrency(
      recurringPriceWithPromoIteration,
      currency,
      getSystemLocale()
    ),
    isCompleted: false,
    helperText: fbt("Discounted price"),
  };
}

interface GetSingleMonthPromoAfterTodayStepConfig {
  fbt: FBT;
  currency: string | null;
  firstPaymentCycles?: number;
  membershipStartDateString?: string;
  recurringPriceWithPromoIteration: number;
}
function getSingleMonthPromoAfterTodayStep({
  fbt,
  currency,
  firstPaymentCycles,
  membershipStartDateString,
  recurringPriceWithPromoIteration,
}: GetSingleMonthPromoAfterTodayStepConfig) {
  return {
    leftText: getFirstDayOfNextMonthAfterNMonths({
      addedMonths:
        (firstPaymentCycles || 0) +
        monthDifference(
          new Date(),
          membershipStartDateString
            ? parseDay(membershipStartDateString)
            : new Date()
        ),
      formatString: "MMMM d, yyyy",
    }),
    rightText: formatCurrency(
      recurringPriceWithPromoIteration,
      currency,
      getSystemLocale()
    ),
    isCompleted: false,
    helperText: fbt("Discounted price"),
  };
}

interface GetPromoEndStepConfig {
  fbt: FBT;
  currency: string | null;
  firstPaymentCycles?: number;
  membershipStartDateString?: string;
  subscriptionIterations: number;
  subscriptionPromotionEndsAt?: string;
  recurringPrice: number;
}

function getPromoEndStep({
  fbt,
  currency,
  firstPaymentCycles = 0,
  membershipStartDateString,
  subscriptionIterations,
  recurringPrice,
  subscriptionPromotionEndsAt,
}: GetPromoEndStepConfig) {
  return {
    leftText:
      // subscriptionIterations = n (or less) and fisrtPaymentCycles = n
      // in this case promo ends before the cycles so we can not use it
      // for the reccurring price start date we need to calculate the recurring price date
      subscriptionIterations <= firstPaymentCycles
        ? getFirstDayOfNextMonthAfterNMonths({
            addedMonths:
              firstPaymentCycles +
              monthDifference(
                new Date(),
                membershipStartDateString
                  ? parseDay(membershipStartDateString)
                  : new Date()
              ),
            formatString: "MMMM d, yyyy",
          })
        : subscriptionPromotionEndsAt || "",
    rightText: formatCurrency(recurringPrice, currency, getSystemLocale()),
    isCompleted: false,
    helperText: fbt("Monthly payment"),
  };
}
